import $ from 'jquery';
import { Logger, Matrix, namespace } from '../../../../../../FrontendBundle/Resources/js/ofcs';
import Draggable from '../../canvas/draggable';
import RotateItemCommand from '../../command/rotateItemCommand';

let handleSymbolsAlreadyInitialized = false;
const DEFAULT_POINTER_DISTANCE = 120;

/**
 * Represents the rotate handle on a svg canvas.
 *
 * @class   RotateHandle
 */
export default class RotateHandle {
    /**
     * Distance between items center and handles pointer
     *
     * @protected
     * @property {Number} pointerDistance
     */

    /**
     * Item which will be rotated
     *
     * @protected
     * @property {ItemInterface} item
     */

    /**
     * DOM element for the line between center and pointer
     *
     * @private
     * @property {SVGLineElement} elementLine
     */

    /**
     * DOM element for the point in the center
     *
     * @private
     * @property {SVGUseElement} elementCenter
     */

    /**
     * DOM element for the pointer
     *
     * @private
     * @property {SVGLineElement} elementPointer
     */

    /**
     * Drag and Drop state.
     *
     * @private
     * @property {Boolean} draggin
     */

    /**
     * Timeout for showing the handle after moving it
     *
     * @private
     * @property {Number} _updatetimeout Timeout ID
     */

    /**
     * Initializes a rotate handle
     *
     * @param {ItemInterface} item Item to rotate
     * @param {Canvas} canvas Canvas which receives the handle
     */
    constructor(item, canvas) {
        const handlesLayer = canvas.getLayerManager().getLayer('ui.handles');
        const center = item.get('position');

        this.item = item;
        this.canvas = canvas;
        this.pointerDistance = DEFAULT_POINTER_DISTANCE;
        this.draggin = false;

        this.initializeHandleSymbols();
        this.elementScale = this.initializeScale();

        item.on('change', (...args) => this.onItemChange(...args));
        canvas.on('zoomchange panchange', (...args) => this.updateElementsPositions(...args));

        this.elementLineBg = $(handlesLayer.line(center.x, center.y, center.x, center.y, {
            stroke: 'rgba(0, 0, 0, .3)',
            strokeWidth: 4,
            strokeLineCap: 'round',
            class: 'rotate-line-bg',
        }));
        this.elementLine = $(handlesLayer.line(center.x, center.y, center.x, center.y, {
            stroke: 'white',
            strokeWidth: 1.5,
            class: 'rotate-line',
        }));

        this.elementPointer = $(handlesLayer.circle(center.x, center.y, 9, {
            fill: 'url(#handleRotateBg)',
            stroke: 'url(#handleRotateStrokeColor)',
            strokeWidth: 0.5,
            style: 'cursor: pointer',
            class: 'handle-rotate',
        })).data('item', item);

        const dragAndDrop = new Draggable(canvas, this.elementPointer);
        dragAndDrop.on('dragStart', (...args) => this.onPointerDragStart(...args));
        dragAndDrop.on('drag', (...args) => this.onPointerDrag(...args));
        dragAndDrop.on('dragStop', (...args) => this.onPointerDragStop(...args));

        this.updateElementsPositions();
    }

    /**
     * Initializes elements for the rotation scale
     *
     * @return {SvgElement}
     * @protected
     */
    initializeScale() {
        const handlesLayer = this.canvas.getLayerManager().getLayer('ui.handles');
        const svg = handlesLayer.getSvg();
        const scaleGroup = $(handlesLayer.group()).hide();

        // Creating a circle
        const backgroundPath = [
            `M${-DEFAULT_POINTER_DISTANCE},0`,
            `a${DEFAULT_POINTER_DISTANCE},${DEFAULT_POINTER_DISTANCE} 0 1,0 ${DEFAULT_POINTER_DISTANCE * 2},0`,
            `a${DEFAULT_POINTER_DISTANCE},${DEFAULT_POINTER_DISTANCE} 0 1,0 ${-DEFAULT_POINTER_DISTANCE * 2},0`,
        ].join(' ');

        const stepMarkerPath = this.item
            .supportedRotationSteps
            .map((degree) => {
                const matrix = new Matrix().rotate(degree);
                const startPoint = DEFAULT_POINTER_DISTANCE - (degree % 45 === 0 ? 20 : 10);

                return [
                    `M${matrix.x(startPoint, 0)},${matrix.y(startPoint, 0)}`,
                    `L${matrix.x(DEFAULT_POINTER_DISTANCE, 0)},${matrix.y(DEFAULT_POINTER_DISTANCE, 0)}`,
                ].join(' ');
            })
            .join(' ');

        svg.path(scaleGroup, `${backgroundPath} ${stepMarkerPath}`, {
            stroke: 'rgba(0, 0, 0, .3)',
            strokeWidth: 8,
            fill: 'none',
            strokeLineCap: 'round',
        });

        this.elementScaleInner = $(svg.path(scaleGroup, stepMarkerPath, {
            stroke: 'white',
            strokeWidth: 1.5,
            fill: 'none',
            strokeOpacity: 0.2,
        }));

        this.elementScaleOuter = $(svg.circle(scaleGroup, 0, 0, DEFAULT_POINTER_DISTANCE, {
            stroke: 'white',
            strokeWidth: 1.5,
            fill: 'none',
        }));

        return scaleGroup;
    }

    /**
     * Callback method which is called when dragging of pointer starts
     *
     * @protected
     * @param {Object} event Drag event. See Draggable#drag
     */
    onPointerDragStart(event) {
        Logger.debug('rotateStart', event);

        this.draggin = true;
        this.canvas.focus();

        this.elementScale.show();

        this.command = new RotateItemCommand(
            this.item,
            undefined,
            this.item.getBounds().getCenter(),
        );
    }

    /**
     * Callback method which is called each movement while dragging
     *
     * @protected
     * @param {Object} event Drag event. See Draggable#drag
     */
    onPointerDrag({ point }) {
        const center = this.getCenter();
        let rotation = center.getAngleTo(point);
        const distance = center.getDistanceTo(point);
        this.pointerDistance = distance * this.canvas.options.zoom;
        const isSnappingAllowed = this.item.supportedRotationSteps && this.item.supportedRotationSteps.length > 0;

        if (isSnappingAllowed && (this.pointerDistance < DEFAULT_POINTER_DISTANCE || !this.item.supportsFreeRotation)) {
            rotation = this.item.getSupportedRotationStep(rotation);
            this.highlightScaleOuter();
            this.updateElementsPositions();
        } else {
            this.highlightScaleInner();
        }

        if (this.item.supportsInitialRotation) {
            rotation += this.item.get('initialRotation') || 0;
            rotation %= 360;
        }

        this.command.setRotation(rotation);
        this.canvas.getCommandExecutor().execute(this.command);
    }

    /**
     * Callback method which is called when dragging of pointer ends
     *
     * @protected
     * @param {Object} event Drag event. See Draggable#drag
     */
    onPointerDragStop(event) {
        Logger.debug('rotateStop', event);

        this.elementScale.hide();
        this.draggin = false;

        this.updateElementsPositions();

        this.canvas.getHistory().addCommand(this.command);
        delete this.command;
    }

    /**
     * Updates the position of internal DOM elements based on the attributes
     * of #item.
     *
     * @protected
     */
    updateElementsPositions() {
        const { options } = this.canvas;
        const center = this.getCenter().clone();
        const pointer = center.clone();
        const positionMatrix = new Matrix();
        const zoomMatrix = new Matrix();
        const pointerMatrix = new Matrix();
        const halfWidth = (this.item.get('width') / 2) * options.zoom;
        let distance = this.pointerDistance;
        const handleOffset = distance - halfWidth;

        zoomMatrix
            .scale(parseFloat(options.zoom))
            .translate(parseInt(options.panX, 10), parseInt(options.panY, 10));

        center.transform(zoomMatrix);
        pointer.transform(zoomMatrix);

        if (!this.draggin && Math.abs(handleOffset) < 20) {
            distance -= handleOffset;
            distance += handleOffset < 0 ? -20 : 20;
        }

        pointerMatrix
            .rotate(this.item.getNormalizedRotation(), center)
            .translate(distance, 0);

        pointer.transform(pointerMatrix);

        positionMatrix.translate(center.x, center.y);

        this.elementScale.attr('transform', positionMatrix.toString());

        this.elementLineBg.attr({
            x1: center.x,
            y1: center.y,
            x2: pointer.x,
            y2: pointer.y,
        });

        this.elementLine.attr({
            x1: center.x,
            y1: center.y,
            x2: pointer.x,
            y2: pointer.y,
        });

        this.elementPointer.attr({
            cx: pointer.x,
            cy: pointer.y,
        });
    }

    showElements() {
        this.elementPointer.show();
        this.elementLine.show();
        this.elementLineBg.show();
    }

    hideElements() {
        this.elementPointer.hide();
        this.elementLine.hide();
        this.elementLineBg.hide();
    }

    /**
     * Callback which is called each time an attribute of #item is changed.
     *
     * @param {ItemInterface} item The item which was modified. Equal to #item
     */
    onItemChange(item) {
        if (!this.draggin && item.hasChangedProperty('position', 'width')) {
            clearTimeout(this._updatetimeout);
            this.hideElements();
            this._updatetimeout = setTimeout(() => {
                delete this.center;
                this.updateElementsPositions();
                this.showElements();
            }, 300);
        } else if (item.hasChangedProperty('rotation')) {
            this.updateElementsPositions();
        }
    }

    /**
     * Removes the DOM nodes from the canvas and deactivates all event listeners
     */
    remove() {
        // Remove all listeners from item
        this.item.off(null, null, this);
        // Remove elements from DOM
        this.elementLine.remove();
        this.elementLineBg.remove();
        this.elementPointer.remove();
        this.elementScale.remove();
    }

    /**
     * Initializes handle symbols for reuse. All symbols are placed
     * in the global `<defs>' node. This method will be canceled if
     * symbols are already initialized.
     *
     * @protected
     */
    initializeHandleSymbols() {
        const layerManager = this.canvas.getLayerManager();
        const handlesLayer = layerManager.getLayer('ui.handles');
        const svg = handlesLayer.getSvg();
        const defs = handlesLayer.getDefs();

        if (handleSymbolsAlreadyInitialized) return;

        const symbol = svg.symbol(defs, 'handleRotatePoint', 0, 0, 10, 10);

        svg.linearGradient(
            defs,
            'handleRotateBg',
            [[0, '#62C462'], [1, '#51A351']],
            0,
            0,
            0,
            1,
        );
        svg.linearGradient(
            defs,
            'handleRotateStrokeColor',
            [[0.5, '#51A351'], [1, '#387038']],
            0,
            0,
            0,
            1,
        );
        svg.circle(symbol, 5, 5, 5, {
            fill: 'black',
            stroke: 'none',
            opacity: 0.2,
            class: 'handle-rotate-shadow',
        });
        svg.circle(symbol, 5, 5, 4, {
            fill: 'url(#handleRotateBg)',
            stroke: 'url(#handleRotateStrokeColor)',
            strokeWidth: 0.5,
            style: 'cursor: pointer',
            class: 'handle-rotate',
        });

        handleSymbolsAlreadyInitialized = true;
    }

    /**
     * @private
     */
    highlightScaleOuter() {
        this.elementScaleInner.attr('stroke-opacity', 1);
        this.elementScaleOuter.attr('stroke-opacity', 0.2);
    }

    /**
     * @private
     */
    highlightScaleInner() {
        this.elementScaleInner.attr('stroke-opacity', 0.2);
        this.elementScaleOuter.attr('stroke-opacity', 1);
    }

    getCenter() {
        if (!this.center) {
            this.center = this.item.getAbsoluteBounds().getCenter().clone();
        }
        return this.center;
    }
}

namespace('Ofcs.Editor.Svg.Handle');
window.Ofcs.Editor.Svg.Handle.RotateHandle = RotateHandle;
